import React, { useEffect, useRef, useState } from 'react';
import { portfolioData } from '../data/mock';
import { ArrowUpRight, BarChart3, Server } from 'lucide-react';
import { Badge } from './ui/badge';

function useInView(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return visible;
}

export default function PortfolioSection() {
  const sectionRef = useRef(null);
  const visible = useInView(sectionRef);
  const [activeFilter, setActiveFilter] = useState('All');

  const categories = ['All', ...new Set(portfolioData.map((p) => p.category))];
  const filtered = activeFilter === 'All' ? portfolioData : portfolioData.filter((p) => p.category === activeFilter);

  return (
    <section id="portfolio" className="relative" ref={sectionRef}>
      <div className="section-container">
        <div className={`fade-in-up ${visible ? 'visible' : ''}`}>
          <p className="section-label">// Featured Work</p>
          <h2 className="section-title">Portfolio & Case Studies</h2>
          <div className="glow-line w-20 mb-8" />
        </div>

        {/* Filter tabs */}
        <div className={`flex flex-wrap gap-2 mb-10 fade-in-up ${visible ? 'visible' : ''}`} style={{ transitionDelay: '0.1s' }}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`px-4 py-2 text-xs font-['JetBrains_Mono'] tracking-wider rounded-md border transition-colors duration-300 ${
                activeFilter === cat
                  ? 'bg-[#00e5ff]/10 border-[#00e5ff]/40 text-[#00e5ff]'
                  : 'bg-transparent border-[#1e293b] text-[#64748b] hover:border-[#94a3b8]/30 hover:text-[#94a3b8]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project, idx) => (
            <div
              key={project.id}
              className={`cyber-card group cursor-pointer fade-in-up ${visible ? 'visible' : ''}`}
              style={{ transitionDelay: `${0.15 + idx * 0.1}s` }}
            >
              {/* Image */}
              <div className="relative h-44 overflow-hidden rounded-t-xl">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111827] via-transparent to-transparent" />
                <div className="absolute top-3 right-3">
                  <Badge className="bg-[#06080d]/70 text-[#00e5ff] border-[#00e5ff]/30 text-[10px] font-['JetBrains_Mono'] backdrop-blur-sm">
                    {project.category}
                  </Badge>
                </div>
                <div className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-[#00e5ff]/10 border border-[#00e5ff]/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 backdrop-blur-sm">
                  <ArrowUpRight className="w-4 h-4 text-[#00e5ff]" />
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="text-base font-semibold text-[#f1f5f9] mb-2 group-hover:text-[#00e5ff] transition-colors duration-300">
                  {project.title}
                </h3>
                <p className="text-sm text-[#94a3b8] leading-relaxed mb-4 line-clamp-2">
                  {project.description}
                </p>

                {/* Metrics */}
                <div className="flex items-center gap-4 mb-4 text-xs">
                  <span className="flex items-center gap-1.5 text-[#00ff88]">
                    <BarChart3 className="w-3.5 h-3.5" /> {project.metrics.impact}
                  </span>
                  <span className="flex items-center gap-1.5 text-[#64748b]">
                    <Server className="w-3.5 h-3.5" /> {project.metrics.scale}
                  </span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5">
                  {project.tags.map((tag) => (
                    <span key={tag} className="cyber-tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
